/**
 * stable store for path a logged-out user was trying to reach before
 * being bounced to an auth page (e.g. a shared /project/:id link)
 * - backed by sessionStorage so path survives full reload & clears when tab closes
 * - stamped with a short TTL so an intent the user never acted on doesn't fire
 *   on some unrelated login later in the same tab
 */

const KEY = "bds_post_login_redirect";

// 5 min expiration for redirect key
const TTL_MS = 5 * 60 * 1000;

const AUTH_PATHS = ["/signin", "/signup", "/reset", "/set-password", "/login"];

// handle both sate.redirect and state.background paths to respect both guest-route
// guards & in-app gates
const toPath = dest => {
  if (typeof dest === "string") return dest;
  if (dest && typeof dest.pathname === "string") {
    return `${dest.pathname}${dest.search || ""}${dest.hash || ""}`;
  }
  return null;
};

const isSafeRedirect = path =>
  typeof path === "string" &&
  path.startsWith("/") &&
  !path.startsWith("//") &&
  !AUTH_PATHS.some(authPath => path.startsWith(authPath));

export const setPostLoginRedirect = dest => {
  const path = toPath(dest);
  if (!isSafeRedirect(path)) return;
  try {
    sessionStorage.setItem(
      KEY,
      JSON.stringify({ path, expiresAt: Date.now() + TTL_MS })
    );
  } catch (e) {
    console.error(e);
  }
};

// user backed out of the auth modal, so drop the intent rather than replay it
// on some later, unrelated login
export const clearPostLoginRedirect = () => {
  try {
    sessionStorage.removeItem(KEY);
  } catch (e) {
    console.error(e);
  }
};

export const consumePostLoginRedirect = () => {
  try {
    const raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    if (!raw) return null;
    const { path, expiresAt } = JSON.parse(raw);
    if (expiresAt && Date.now() > expiresAt) return null;
    return isSafeRedirect(path) ? path : null;
  } catch (e) {
    // corrupt or legacy (pre-TTL bare-string) value, just fall back to "/"
    return null;
  }
};

/**
 * pending actions a guest was gated on (e.g. download, add to playlist), carried
 * on the post-login return URL so the action can resume once authenticated
 */
export const INTENTS = {
  DOWNLOAD: "download",
  PLAYLIST: "playlist",
  FAVORITE: "favorite",
  EDITOR: "editor"
};
const INTENT_PARAM = "intent";
const INTENT_TRACK_PARAM = "intentTrackId";

export const withIntent = (location, { type, trackId }) => {
  const params = new URLSearchParams(location.search);
  params.set(INTENT_PARAM, type);
  params.set(INTENT_TRACK_PARAM, trackId);

  return `${location.pathname}?${params.toString()}${location.hash || ""}`;
};

export const readIntent = search => {
  const params = new URLSearchParams(search);
  const type = params.get(INTENT_PARAM);
  const trackId = params.get(INTENT_TRACK_PARAM);

  if (!trackId || !Object.values(INTENTS).includes(type)) return null;

  return { type, trackId };
};

export const hasIntent = dest => {
  const path = toPath(dest);

  return !!path && !!readIntent(path.split("?")[1]?.split("#")[0]);
};

export const stripIntent = location => {
  const params = new URLSearchParams(location.search);
  params.delete(INTENT_PARAM);
  params.delete(INTENT_TRACK_PARAM);

  const search = params.toString();

  return {
    pathname: location.pathname,
    search: search ? `?${search}` : "",
    hash: location.hash
  };
};
